<script setup lang="ts">
import type { MemoryEvent } from '@/api/memory/memoryType';
import { Icon } from '@iconify/vue';
import { computed } from 'vue';
import MemoryEventDetail from './MemoryEventDetail.vue';

const props = defineProps<{
  events: MemoryEvent[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  search: string;
  eventType: string;
}>();

const emit = defineEmits<{
  pageChange: [page: number];
  retry: [];
}>();

const emptyMessage = computed(() => props.search || props.eventType
  ? '没有符合当前筛选条件的活动记录。'
  : '暂时没有可展示的活动记录。');
</script>

<template>
  <section>
    <div v-if="loading && events.length === 0" class="space-y-3 py-2">
      <div v-for="index in 4" :key="index" class="h-20 animate-pulse rounded-md bg-muted" />
    </div>

    <div v-else-if="events.length === 0" class="flex flex-col items-center justify-center gap-3 py-12 text-center">
      <Icon icon="lucide:inbox" class="size-8 text-muted-foreground" />
      <p class="text-sm text-muted-foreground">
        {{ emptyMessage }}
      </p>
      <button type="button" class="text-sm text-primary hover:underline" @click="emit('retry')">
        重新加载
      </button>
    </div>

    <div v-else class="relative">
      <div v-if="loading" class="absolute inset-0 z-10 flex justify-center bg-background/60 pt-8">
        <Icon icon="lucide:loader-circle" class="size-5 animate-spin text-primary" />
      </div>
      <MemoryEventDetail v-for="event in events" :key="event.id" :event="event" />
    </div>

    <div v-if="totalPages > 1" class="mt-5 flex items-center justify-between gap-3">
      <p class="text-xs text-muted-foreground">
        第 {{ currentPage }} / {{ totalPages }} 页
      </p>
      <div class="flex items-center gap-2">
        <button
          type="button"
          title="上一页"
          aria-label="上一页"
          class="inline-flex size-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="currentPage <= 1 || loading"
          @click="emit('pageChange', currentPage - 1)"
        >
          <Icon icon="lucide:chevron-left" class="size-4" />
        </button>
        <button
          type="button"
          title="下一页"
          aria-label="下一页"
          class="inline-flex size-8 items-center justify-center rounded-md border text-muted-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-40"
          :disabled="currentPage >= totalPages || loading"
          @click="emit('pageChange', currentPage + 1)"
        >
          <Icon icon="lucide:chevron-right" class="size-4" />
        </button>
      </div>
    </div>
  </section>
</template>
